import { api } from '../api.js';
import { project as store } from '../project-store.js';
import { state } from '../state.js';
import { RouterLink } from '../router.js';
import { scoreColor, humanDate, Empty } from '../ui.js';

// График оценок по темам во времени. Рисуем сами через SVG: тянуть Chart.js
// ради одной ломаной — несоразмерно, а шага сборки у нас нет.

export const Progress = {
  components: { RouterLink, Empty },
  data() {
    return { loading: true, error: '', points: [], weak: [], plan: [],
      coverage: null, causes: [], hidden: new Set() };
  },
  computed: {
    projectId() { return state.projectId; },
    project() { return state.projects.find((p) => p.id === state.projectId); },

    topics() {
      return [...new Set(this.points.map((p) => p.topic))].sort();
    },

    /** Одна ломаная на тему: X — номер собеса по порядку, Y — оценка. */
    series() {
      const sessions = [...new Set(this.points.map((p) => p.session_id))].sort((a, b) => a - b);
      const xOf = new Map(sessions.map((id, i) => [id, i]));
      return this.topics.filter((t) => !this.hidden.has(t)).map((topic, i) => ({
        topic,
        color: PALETTE[i % PALETTE.length],
        dots: this.points.filter((p) => p.topic === topic).map((p) => ({
          x: xOf.get(p.session_id),
          y: p.score,
          session: p.session_id,
          date: p.created_at,
        })),
      }));
    },

    /** Пройденное и слепые зоны — двумя списками: вперемешку это не читается. */
    coveredRows() { return this.coverage ? this.coverage.rows.filter((r) => r.asked) : []; },
    blindRows() { return this.coverage ? this.coverage.rows.filter((r) => !r.asked) : []; },
    coverPercent() {
      if (!this.coverage?.total) return 0;
      return Math.round((this.coverage.covered / this.coverage.total) * 100);
    },

    sessionCount() {
      return new Set(this.points.map((p) => p.session_id)).size;
    },

    geometry() {
      const w = 900;
      const h = 240;
      const pad = { l: 34, r: 16, t: 14, b: 26 };
      const cols = Math.max(1, this.sessionCount - 1);
      const x = (i) => pad.l + (cols ? (i / cols) * (w - pad.l - pad.r) : 0);
      const y = (v) => pad.t + (1 - (v - 1) / 4) * (h - pad.t - pad.b);
      return { w, h, pad, x, y };
    },
  },
  async created() { await this.load(); },
  methods: {
    scoreColor, humanDate,

    async load() {
      if (!this.projectId) { this.loading = false; return; }
      try {
        const data = await api.get(`/api/projects/${this.projectId}/progress`);
        this.points = data.points;
        // Слабые темы — общие: их же читает выбор формата на главной вкладке.
        // Раньше они грузились дважды, разными запросами, и расходились.
        this.weak = data.weak;
        store.weak = data.weak || [];
        this.plan = data.plan || [];
        this.coverage = data.coverage || null;
        this.causes = data.causes || [];
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    path(dots) {
      const { x, y } = this.geometry;
      return dots.map((d, i) => `${i ? 'L' : 'M'}${x(d.x).toFixed(1)},${y(d.y).toFixed(1)}`).join(' ');
    },

    toggle(topic) {
      const next = new Set(this.hidden);
      if (next.has(topic)) next.delete(topic); else next.add(topic);
      this.hidden = next;
    },

    colorOf(topic) {
      return PALETTE[this.topics.indexOf(topic) % PALETTE.length];
    },
  },
  template: `
    <div>
      <p class="sub" v-if="project">Оценки по темам от собеса к собесу.</p>

      <p class="err" v-if="error">{{ error }}</p>

      <Empty v-if="!loading && !points.length"
             title="Данных пока нет"
             hint="Оценки по темам появляются после завершённого собеса. Проведи хотя бы один.">
      </Empty>

      <template v-else-if="points.length">
        <div class="card">
          <svg class="chart" :viewBox="'0 0 ' + geometry.w + ' ' + geometry.h" preserveAspectRatio="none">
            <g v-for="v in [1,2,3,4,5]" :key="v">
              <line :x1="geometry.pad.l" :x2="geometry.w - geometry.pad.r"
                    :y1="geometry.y(v)" :y2="geometry.y(v)" stroke="#262c34" stroke-width="1"/>
              <text :x="geometry.pad.l - 10" :y="geometry.y(v) + 4"
                    fill="#626c7a" font-size="11" text-anchor="end">{{ v }}</text>
            </g>
            <g v-for="s in series" :key="s.topic">
              <path :d="path(s.dots)" :stroke="s.color" stroke-width="2" fill="none"
                    stroke-linejoin="round" stroke-linecap="round"/>
              <circle v-for="(d, i) in s.dots" :key="i"
                      :cx="geometry.x(d.x)" :cy="geometry.y(d.y)" r="3.5" :fill="s.color">
                <title>{{ s.topic }}: {{ d.y }} · собес #{{ d.session }}</title>
              </circle>
            </g>
          </svg>

          <div class="legend">
            <span v-for="t in topics" :key="t" @click="toggle(t)"
                  style="cursor:pointer"
                  :style="{ opacity: hidden.has(t) ? 0.35 : 1 }">
              <i :style="{ background: colorOf(t) }"></i>{{ t }}
            </span>
          </div>
          <p class="faint" style="margin-top:10px">
            По горизонтали — собесы по порядку, по вертикали — оценка от 1 до 5.
            Клик по теме в легенде прячет её.
          </p>
        </div>

        <h2>Слабые места сейчас</h2>
        <div class="card" v-if="weak.length">
          <div class="scores">
            <div class="score-row" v-for="w in weak" :key="w.topic">
              <span>{{ w.topic }}</span>
              <span class="score-bar">
                <i :style="{ width: (w.score * 20) + '%', background: scoreColor(w.score) }"></i>
              </span>
              <span class="score-val">{{ w.score }}</span>
            </div>
          </div>
          <p class="faint" style="margin-top:12px">
            Тренажёр в проекте гоняет именно эти темы.
          </p>
        </div>
        <div class="card" v-else>
          <p class="muted" style="margin:0">Тем с оценкой ниже четвёрки нет. Хороший знак.</p>
        </div>

        <template v-if="coverage && coverage.total">
          <h2>Карта покрытия</h2>
          <div class="card">
            <p class="note" style="margin-top:0">
              Оценки бывают только у тем, которые у тебя спрашивали. Режут же
              обычно на тех, о которых не знаешь, что их не знаешь.
            </p>

            <div class="cover-head">
              <b>{{ coverage.covered }}</b>
              <span class="faint">из {{ coverage.total }} тем направления</span>
              <span class="cover-bar"><i :style="{ width: coverPercent + '%' }"></i></span>
            </div>

            <p class="cover-sub" v-if="coveredRows.length">Спрашивали</p>
            <div class="cover" v-if="coveredRows.length">
              <span v-for="c in coveredRows" :key="c.topic" class="cell known"
                    :style="{ borderLeftColor: scoreColor(c.score) }">
                <span class="name">{{ c.topic }}</span>
                <b :style="{ color: scoreColor(c.score) }">{{ c.score }}</b>
              </span>
            </div>

            <p class="cover-sub" v-if="blindRows.length">Ни разу не спрашивали</p>
            <div class="cover" v-if="blindRows.length">
              <span v-for="c in blindRows" :key="c.topic" class="cell blind">
                <span class="name">{{ c.topic }}</span>
              </span>
            </div>

            <p class="faint" v-if="coverage.extra.length" style="margin-top:14px">
              Сверх списка спрашивали: {{ coverage.extra.map(e => e.topic).join(', ') }}.
            </p>
            <p class="faint" v-if="coverage.blind.length" style="margin-top:8px">
              Слепые зоны гоняются в разделе «Повторы», вкладка «Не знаю».
            </p>
          </div>
        </template>

        <template v-if="causes.length">
          <h2>Почему проваливался</h2>
          <div class="card">
            <p class="note" style="margin-top:0">
              Тема говорит, что подтянуть, а причина — как. Читать, проговаривать
              вслух и держать удар — это разные упражнения.
            </p>
            <div class="score-row" v-for="c in causes" :key="c.cause">
              <span>{{ c.cause }}</span>
              <span class="score-bar">
                <i :style="{ width: Math.min(100, c.count * 20) + '%', background: 'var(--warn)' }"></i>
              </span>
              <span class="score-val">{{ c.count }}</span>
            </div>
          </div>
        </template>

        <template v-if="plan.length">
          <h2>Что учить</h2>
          <div class="card">
            <p class="note" style="margin-top:0">План из последнего разбора.</p>
            <div class="study" v-for="(it, i) in plan" :key="'p' + i">
              <span class="prio" :class="'p' + it.priority">{{ it.priority }}</span>
              <div>
                <div><strong v-if="it.topic">{{ it.topic }}.</strong> {{ it.what }}</div>
                <div class="faint" v-if="it.source">{{ it.source }}</div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>`,
};

const PALETTE = [
  '#4b8fd6', '#4a9d6a', '#c9a227', '#b6403f', '#9d6ac0',
  '#3fa8a0', '#c96a3f', '#7ba85a', '#d67ba8', '#6a83c0',
];
